/**
 * Deploy handle contract + the `resolveDeployTarget` precedence resolver.
 *
 * A `DeployHandle` is the live, restorable result of standing up a workspace on
 * a deploy target. `resolveDeployTarget` picks which target+config to use with
 * an explicit override > yaml > default chain.
 */

import { DeployTargetNotFoundError } from "./errors.js";
import { pluginRegistry } from "./internal.js";
import type { PluginRegistry, TargetMeta } from "./registry.js";
import type { DeployTarget } from "./targets.js";

/**
 * Live handle to a deployed workspace. `payload` is the target's own opaque
 * persistence slice (written to `.skaile/deploy/handle.json` and fed back into
 * `restore()`); the registry wrapper never inspects it.
 */
export interface DeployHandle<S = unknown> {
  readonly wsUrl: string;
  /** Redacted form of the auth token, safe to log. */
  readonly wsAuth?: string;
  readonly state: "starting" | "ready" | "stopping" | "stopped" | "errored";
  readonly payload: S;
  waitReady(timeoutMs?: number): Promise<void>;
  health(): Promise<{ healthy: boolean; detail?: string }>;
  logs?(opts?: { sinceMs?: number; follow?: boolean }): AsyncIterable<{ ts: number; line: string }>;
  stop(): Promise<void>;
}

/**
 * List the registered deploy targets as lightweight `{ id, displayName }` rows,
 * the symmetric counterpart to `listDrivers()` / `listConnectors()`. Reads the
 * process-wide `pluginRegistry` by default, so call
 * `registerBuiltinDeployTargets()` (and load any plugins) first — unregistered
 * targets do not appear. Enough to populate a target picker without resolving a
 * config; use `getDeployTarget(id)` for the full target.
 */
export function listDeployTargets(
  registry: PluginRegistry = pluginRegistry,
): readonly TargetMeta[] {
  return registry.list("deployTarget");
}

/**
 * Look up a single registered deploy target by id, or `undefined` if none is
 * registered under that id. Thin wrapper over `pluginRegistry.get`, mirroring
 * `getConnector()`; unlike `resolveDeployTarget` it neither applies the
 * override/yaml/default precedence nor parses a config.
 */
export function getDeployTarget(
  id: string,
  registry: PluginRegistry = pluginRegistry,
): DeployTarget | undefined {
  return registry.get("deployTarget", id);
}

/** Outcome of `resolveDeployTarget`: the chosen target, its parsed config, and where it came from. */
export interface DeployResolution {
  target: DeployTarget;
  config: unknown;
  provenance: { source: "default" | "yaml" | "override"; reason?: string };
}

/**
 * Resolve which deploy target + config to use, with explicit precedence:
 * CLI override > `skaile.yaml` `deploy:` block > the built-in `local` default.
 * Config is parsed through the chosen target's `configSchema`, so validation
 * failures propagate to the caller.
 */
export async function resolveDeployTarget(
  yaml: { deploy?: { target: string; config?: unknown } },
  override?: { target: string; config?: unknown; reason: string },
  registry: PluginRegistry = pluginRegistry,
): Promise<DeployResolution> {
  if (override) {
    const target = registry.get("deployTarget", override.target);
    if (!target) throw new DeployTargetNotFoundError(override.target);
    return {
      target,
      config: target.configSchema.parse(override.config ?? {}),
      provenance: { source: "override", reason: override.reason },
    };
  }
  if (yaml.deploy) {
    const target = registry.get("deployTarget", yaml.deploy.target);
    if (!target) throw new DeployTargetNotFoundError(yaml.deploy.target);
    return {
      target,
      config: target.configSchema.parse(yaml.deploy.config ?? {}),
      provenance: { source: "yaml" },
    };
  }
  const target = registry.get("deployTarget", "local");
  if (!target) throw new DeployTargetNotFoundError("local");
  return {
    target,
    config: target.configSchema.parse({}),
    provenance: { source: "default" },
  };
}
